import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { formatVariableLabel, getVariableTooltip } from "../variable-token";
import type { IVariableMentionOptions } from "./variable-mention.types";
import { variableTokenFromNode } from "./variable-mention.utilities";
import { VariablePill } from "./variable-pill";

/**
 * React node view for a `variableMention` node: a `VariablePill` and its tooltip in an inline `<span>`, so the
 * pill sits in the paragraph's text flow.
 *
 * No handlers are attached and nothing stops event propagation, so ProseMirror still sees the mousedown and
 * turns a click on the pill into a `NodeSelection`, which comes back here as `selected`.
 * @param props.node The ProseMirror node, whose attributes hold the variable.
 * @param props.selected Whether the node is the current `NodeSelection`.
 * @param props.extension The extension, for the consumer's `getIcon` and `getTooltipPrefix`.
 * @returns {JSX.Element} The node view.
 */
export const VariableMentionView = ({ node, selected, extension }: NodeViewProps) => {
    const variable = variableTokenFromNode(node);
    // Tiptap types an extension's options as `any`; the annotation gives them their real shape.
    const { getIcon, getTooltipPrefix }: IVariableMentionOptions = extension.options;

    return (
        <NodeViewWrapper as="span">
            <VariablePill
                label={formatVariableLabel(variable)}
                icon={getIcon?.(variable)}
                tooltip={getVariableTooltip(variable, getTooltipPrefix?.(variable))}
                selected={selected}
            />
        </NodeViewWrapper>
    );
};
